import { z } from "zod";

const fields = z.array(z.strictObject({
  name: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/).refine((s) => !["__proto__", "constructor", "prototype"].includes(s)),
  type: z.enum(["text", "email", "textarea", "number"]),
  required: z.boolean().default(false),
})).min(1).max(30).refine((items) => new Set(items.map((f) => f.name)).size === items.length, "Field names must be unique");
const actions = z.strictObject({
  email: z.email().max(254).optional(),
  webhookUrl: z.url().max(2048).optional(),
}).refine((a) => a.email || a.webhookUrl, "Configure an email and/or webhook action");
export const formConfig = z.strictObject({
  fields,
  actions,
  // This configured destination is checked against the site's owned hostnames by the API.
  // `new URL` is guarded: zod4's `refine` runs even after `z.url()` itself
  // already rejected the value (no short-circuit on the earlier check), so
  // an unguarded `new URL(s)` in here still throws on a value `z.url()` has
  // already failed (a bare path like "/") — and an uncaught throw inside a
  // zod `refine` escapes `safeParse` too. Confirmed live, sa#278's own e2e
  // check, 2026-09-27 (mechanism corrected in review, DevGuru) — turning
  // what should be a clean validation refusal into an unhandled exception
  // through the whole MCP call.
  successUrl: z.url().max(2048).refine((s) => {
    try {
      const u = new URL(s);
      return u.protocol === "https:" && !u.username && !u.password;
    } catch {
      return false;
    }
  }, "Use an HTTPS URL on this site"),
  replyToField: z.string().optional(),
  turnstileEnabled: z.boolean().default(true),
  retentionDays: z.number().int().min(1).max(365).default(90),
  enabled: z.boolean().default(true),
}).refine((c) => !c.replyToField || c.fields.some((f) => f.name === c.replyToField && f.type === "email"), "replyToField must name an email field");
export const createFormInput = z.strictObject({ config: formConfig });
export const updateFormInput = z.strictObject({ formId: z.string().min(1), config: formConfig });
export const removeFormInput = z.strictObject({ formId: z.string().min(1) });
export const listFormSubmissionsInput = z.strictObject({
  siteId: z.string(), formId: z.string(),
  limit: z.number().int().min(1).max(100).default(50),
  before: z.string().regex(/^\d+:sub_[a-f0-9]{32}$/).optional(),
});
export const formResult = z.strictObject({
  formId: z.string(), shortcode: z.string(), submitUrl: z.url(), turnstileSiteKey: z.string(),
});
export const removedFormResult = z.strictObject({ formId: z.string(), removed: z.boolean() });
export const formSubmissionsResult = z.strictObject({
  submissions: z.array(z.strictObject({
    id: z.string(), values: z.record(z.string(), z.string()),
    status: z.enum(["pending", "delivered", "failed"]), attempts: z.number(),
    lastError: z.string().nullable(), createdAt: z.number(), updatedAt: z.number(),
  })),
  nextBefore: z.string().optional(),
});
export type FormConfig = z.infer<typeof formConfig>;
